import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Trash, ArrowLeft, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TOOLS = [
  { key: "",              label: "All tools (any report)" },
  { key: "divisional",    label: "Divisional Charts (D1–D60)" },
  { key: "twenty_year",   label: "20-Year Forecast" },
  { key: "navamsa",       label: "D9 Navamsa" },
  { key: "past_life",     label: "Past Life" },
  { key: "nakshatra",     label: "Nakshatra Deep Read" },
  { key: "bhrigu",        label: "Bhrigu Bindu" },
  { key: "rahu",          label: "Rahu Playbook" },
  { key: "chakras",       label: "Chakra & Purushartha" },
  { key: "planet_effects",label: "9 Planet Effects" },
  { key: "compatibility", label: "Compatibility (Guna Milan)" },
  { key: "varshaphal",    label: "Varshaphal" },
  { key: "numerology_deep",label:"Deep Numerology" },
];

export default function AdminPdfCachePurgePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tool, setTool] = useState("divisional");
  const [email, setEmail] = useState("");
  const [reportId, setReportId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!authLoading && !user?.is_super_admin) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-lg mx-auto px-6 py-24 text-center">
          <Warning size={40} weight="duotone" className="mx-auto text-amber-500 mb-3" />
          <div className="font-serif-display text-2xl text-[#3F2D1B]">Super admin only.</div>
        </main>
        <Footer />
      </div>
    );
  }

  const onPurge = async () => {
    if (!tool && !email && !reportId) {
      toast.error("Provide at least one filter (tool, email or report_id).");
      return;
    }
    if (!window.confirm("Purge PDF cache with these filters? Next download will regenerate fresh.")) return;
    setBusy(true); setResult(null);
    try {
      const params = new URLSearchParams();
      if (tool) params.set("tool", tool);
      if (email.trim()) params.set("user_email", email.trim());
      if (reportId.trim()) params.set("report_id", reportId.trim());
      const { data } = await api.post(`/super-admin/reports/purge-pdf-cache?${params.toString()}`);
      setResult(data);
      toast.success(`Purged ${data.modified} of ${data.matched} reports.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-2xl mx-auto px-5 sm:px-6 py-12" data-testid="admin-pdf-cache-purge-page">
        <button onClick={() => navigate("/super-admin")}
          className="text-sm text-muted hover:text-[#3F2D1B] mb-4 inline-flex items-center gap-1"
          data-testid="back-btn">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-3 mb-2">
          <Trash size={26} weight="duotone" className="text-rose-600" />
          <h1 className="font-serif-display text-3xl text-[#3F2D1B] font-light">Purge PDF cache</h1>
        </div>
        <p className="text-muted text-sm mb-8">
          Clears the cached PDF bytes on <code>user_reports</code> docs so the very next
          download regenerates a fresh PDF using the latest renderer code. Use after
          shipping a fix so paying customers get the corrected file.
        </p>

        <div className="rounded-2xl border border-soft bg-white p-5 sm:p-6 space-y-4">
          <label className="block">
            <div className="label-eyebrow text-[10px] text-[#3F2D1B]">Tool (recommended)</div>
            <select value={tool} onChange={(e) => setTool(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md border border-soft bg-bg text-sm"
              data-testid="tool-select">
              {TOOLS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>

          <label className="block">
            <div className="label-eyebrow text-[10px] text-[#3F2D1B]">Customer email (optional)</div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="pavan5119m@gmail.com"
              className="mt-1 w-full px-3 py-2 rounded-md border border-soft bg-bg text-sm"
              data-testid="email-input" />
            <div className="text-[11px] text-muted mt-1">Leave blank to purge for ALL customers who bought this tool.</div>
          </label>

          <label className="block">
            <div className="label-eyebrow text-[10px] text-[#3F2D1B]">Report ID (optional)</div>
            <input type="text" value={reportId} onChange={(e) => setReportId(e.target.value)}
              placeholder="rep_..."
              className="mt-1 w-full px-3 py-2 rounded-md border border-soft bg-bg text-sm"
              data-testid="report-id-input" />
          </label>

          <button onClick={onPurge} disabled={busy}
            className="btn-primary w-full rounded-md py-3 text-sm font-bold tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="purge-btn">
            <Trash size={16} weight="fill" />
            {busy ? "Purging…" : "Purge PDF cache"}
          </button>

          {result && (
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3" data-testid="purge-result">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                <CheckCircle size={16} weight="fill" /> Done
              </div>
              <div className="text-xs text-[#3F2D1B] mt-1">
                Matched <b>{result.matched}</b> · Modified <b>{result.modified}</b>. Next download for
                these reports will regenerate the PDF fresh.
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
